import { WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { LobbyFrame } from './lobby-frame';
import styles from './lobby-screen.module.scss';

/**
 * - `guest`: signed out; signing in or creating an account comes back here, and joins.
 * - `joining`: the join is being sent.
 * - `joined`: the caller has seat 2, and the game has started.
 * - `refused`: the game can't be joined, with the reason the player reads; `onRetry` when trying again may help.
 */
export type JoinGameScreenState =
  | { kind: 'guest'; onCreateAccount: () => void; onSignIn: () => void }
  | { kind: 'joining' }
  | { kind: 'joined'; opponentName: string }
  | { kind: 'refused'; message: string; onRetry?: () => void };

export interface JoinGameScreenProps {
  state: JoinGameScreenState;
  /** Goes to the games list. */
  onShowGames: () => void;
  onBack: () => void;
  className?: string;
}

/** The invite landing page (/join/:code): where an invite link opens. It holds no state: the app joins. */
export const JoinGameScreen: React.FC<JoinGameScreenProps> = ({ state, onShowGames, onBack, className }) => (
  <LobbyFrame title="You're invited to a game" backLabel="Back to home" onBack={onBack} className={className}>
    {state.kind === 'guest' ? (
      <div className={styles.card}>
        <p className={styles.lead}>Sign in or create a free account to join. You'll come straight back to the game.</p>
        <div className={styles.actions}>
          <WordsHappenButton variant="primary" onClick={state.onCreateAccount}>
            Create free account
          </WordsHappenButton>
          <WordsHappenButton onClick={state.onSignIn}>Sign in</WordsHappenButton>
        </div>
      </div>
    ) : null}
    {state.kind === 'joining' ? (
      <p className={styles.status} role="status">
        Joining the game…
      </p>
    ) : null}
    {state.kind === 'joined' ? (
      <div className={styles.card}>
        <p className={styles.lead} role="status">{`You're playing ${state.opponentName}.`}</p>
        <WordsHappenButton variant="primary" onClick={onShowGames}>
          Go to your games
        </WordsHappenButton>
      </div>
    ) : null}
    {state.kind === 'refused' ? (
      <div className={styles.error}>
        <WordsHappenFormAlert title="You can't join this game">{state.message}</WordsHappenFormAlert>
        <div className={styles.inlineActions}>
          {state.onRetry ? (
            <WordsHappenButton size="sm" onClick={state.onRetry}>
              Try again
            </WordsHappenButton>
          ) : null}
          <WordsHappenButton size="sm" onClick={onShowGames}>
            Go to your games
          </WordsHappenButton>
        </div>
      </div>
    ) : null}
  </LobbyFrame>
);
